import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { gsap, ScrollTrigger } from "@/lib/gsap";
import { useReducedMotion } from "@/lib/motion";
import { getLenis, setLenis } from "@/lib/scroll";

const NAV_OFFSET = 72;

function scrollToHash(hash: string) {
  const target = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (!target) return;
  const lenis = getLenis();
  if (lenis) lenis.scrollTo(target, { offset: -NAV_OFFSET, duration: 1.4 });
  else target.scrollIntoView();
}

/**
 * Inertial scrolling driven by the GSAP ticker, so ScrollTrigger and the
 * scroll position never disagree. Also resets the scroll position on route
 * changes and handles `/#section` links.
 */
export function SmoothScroll() {
  const reduced = useReducedMotion();
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (reduced) return;

    const instance = new Lenis({ lerp: 0.11 });
    setLenis(instance);
    instance.on("scroll", ScrollTrigger.update);

    const tick = (time: number) => instance.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      instance.destroy();
      setLenis(null);
    };
  }, [reduced]);

  // Runs before the new page's own layout effects, so its ScrollTriggers are
  // created with the page already at the top.
  useLayoutEffect(() => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
    else window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (!hash) return;
    // Wait for the page (and any pinned sections) to be laid out.
    const timer = window.setTimeout(() => scrollToHash(hash), 120);
    return () => window.clearTimeout(timer);
  }, [pathname, hash]);

  useEffect(() => {
    // Headline metrics change when the web font arrives.
    document.fonts?.ready.then(() => ScrollTrigger.refresh());
  }, []);

  return null;
}
